import classNames from 'clsx';
import type { FormApi } from 'final-form';
import React, { useMemo } from 'react';
import type { SyntheticEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import createForm from '@swf/components/Forms/Form';
import Toggle from '@swf/components/Inputs/Toggle';
import Button from '@swf/components/StarsButton/StarsButton';
import type { CardViewProps } from '@swf/components/StarsMPNotification/config/NotificationTypes';
import { geti18NValueConfig } from '@swf/components/StarsMPNotification/utils';
import LicensedTranslation from '@swf/components/Translation/LicensedTranslation';
import Translation from '@swf/components/Translation/Translation';
import { Currency, ResolverTag } from '@swf/core/enums';
import useResolver from '@swf/core/hooks/useResolver';
import { ErrorCode } from '@swf/core/models/notification';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import { License } from '@swf/core/types/Account';
import type { BonusNotification } from '@swf/core/types/Notification';
import { externalLink, translatedLink } from '@swf/core/utils';
import { toLocaleDateString } from '@swf/core/utils/locales';
import { NotificationContainer } from '@swf/notification';

import sharedStyles from '../../../styles/shared.scss';
import Buttons from '../../Buttons/Buttons';

import {
  TXTCLI_Accept,
  TXTCLI_By_accepting_this_offerX_you_a_ELL,
  TXTCLI_Casino,
  TXTCLI_Click_the_XAcceptX_button_to_g_ELL,
  TXTCLI_CongratulationsX_you_have_been_ELL_1,
  TXTCLI_Decline,
  TXTCLI_Eligible_GamesX_N0,
  TXTCLI_Good_LuckX,
  TXTCLI_LoadingXX,
  TXTCLI_Redeem_your_StarsCoin,
  TXTCLI_Winnings_from_the_Instant_Bonu_ELL,
  TXTCLI_You_have_been_awarded_a_N0_N1__ELL,
  TXTCLI_You_will_need_to_earn_N0_Redem_ELL_2,
  TXTCLI_You_will_need_to_earn_N0_Redem_ELL_5,
  TXTCLI_You_will_need_to_place_N0_Tota_ELL_2,
  TXTCLI_You_will_need_to_wager_N0_in_t_ELL,
  TXTCLI_You_will_need_to_wager_N0_in_t_ELL_3,
  TXTMOB_Something_went_wrongX_Please_t_ELL,
  TXTWCL_A_N0_N1_XXN2XX_Instant_Bonus_i_ELL,
  TXTWCL_A_N0_N1_XXN2XX_Instant_Bonus_i_ELL_1,
  TXTWCL_Balance_requiredX,
  TXTWCL_If_you_wish_to_see_more_inform_ELL,
  TXTWCL_Please_indicate_if_you_would_l_ELL,
  TXTWCL_Purchase_Bonus,
  TXTWCL_You_have_a_new_bonus_offer_ava_ELL,
  TXTWCL_You_have_insufficient_balance__ELL
} from './Issued.messages.json';

enum Accept {
  YES = 'yes',
  NO = 'no'
}

interface FormValues {
  bonusAccept: Accept;
}

const Form = createForm<FormValues>();

const Issued: React.FC<
  CardViewProps & { cardData: BonusNotification & { errorCode?: ErrorCode } }
> = ({
  cardData: {
    attributes,
    clearBets,
    clearPoints: cp,
    clearStakes,
    costCash = 0,
    costFpp = 0,
    currency,
    description,
    expiredDate,
    errorCode,
    hasError,
    initialAmount = 0,
    longEligibleGameNames,
    notificationId,
    redemptionLimit,
    rollId,
    shortEligibleGameNames,
    status,
    submitted,
    timeZone,
    type
  },
  viewConfig: { title: titleKey, actions }
}) => {
  const license = useSelector(select.account.getLicense);
  const language = useSelector(select.locale.get);
  const instantBonusTermsCash = useResolver(ResolverTag.InstantBonusTermsCash);
  const instantBonusTermsCashCap = useResolver(ResolverTag.InstantBonusTermsCashCap);

  const { modal, notification } = useDispatch<Dispatch>();
  const eligibleGameNames = shortEligibleGameNames || longEligibleGameNames || '';
  const clearPoints = useMemo(() => Math.floor(parseInt(cp as string, 10) / 1000) / 100, [cp]);

  const title = costCash
    ? { tag: TXTWCL_Purchase_Bonus, values: {} }
    : costFpp
    ? { tag: TXTCLI_Redeem_your_StarsCoin, values: {} }
    : titleKey;

  const viewBonuses = (e: SyntheticEvent) => {
    e.preventDefault();
    modal.replace([{ path: `/account/my-stars/bonus/instant-bonuses/${rollId}` }]);
  };

  const handleSubmit = (form: FormApi<FormValues>) => {
    const {
      values: { bonusAccept }
    } = form.getState();
    notification.acceptBonus({
      rollId,
      status,
      notificationId,
      acceptBonus: bonusAccept === Accept.YES
    });
    modal.close('/mpnotifications');
  };

  return (
    <NotificationContainer
      title={<Translation {...title.tag} values={geti18NValueConfig(title.values, attributes)} />}
      bodyClass={classNames(sharedStyles.containerBody, sharedStyles.alignLeft)}
    >
      <Form
        onSubmit={() => undefined}
        initialValues={{ bonusAccept: Accept.YES }}
        render={({ form }) => (
          <>
            {(!!costCash || !!costFpp) && (
              <LicensedTranslation tagName="p" {...TXTWCL_You_have_a_new_bonus_offer_ava_ELL} />
            )}

            {!costCash && !costFpp && (
              <>
                <LicensedTranslation
                  include={[License.IT, License.PT]}
                  tagName="p"
                  {...TXTCLI_CongratulationsX_you_have_been_ELL_1}
                  values={{
                    0: description,
                    1: <CurrencyAmount value={initialAmount} currency={currency} />
                  }}
                />
                <LicensedTranslation
                  exclude={[License.IT, License.PT]}
                  tagName="p"
                  {...TXTCLI_You_have_been_awarded_a_N0_N1__ELL}
                  values={{
                    0: <CurrencyAmount value={initialAmount} currency={currency} />,
                    1: <Translation {...TXTCLI_Casino} />,
                    2: description
                  }}
                />
              </>
            )}

            {!!costCash && (
              <LicensedTranslation
                exclude={[License.IT, License.PT]}
                tagName="p"
                {...TXTWCL_A_N0_N1_XXN2XX_Instant_Bonus_i_ELL}
                values={{
                  0: <CurrencyAmount value={initialAmount} currency={currency} />,
                  1: <Translation {...TXTCLI_Casino} />,
                  2: description,
                  3: <CurrencyAmount value={costCash} currency={currency} />
                }}
              />
            )}

            {!!costFpp && (
              <LicensedTranslation
                exclude={[License.IT, License.PT]}
                tagName="p"
                {...TXTWCL_A_N0_N1_XXN2XX_Instant_Bonus_i_ELL_1}
                values={{
                  0: <CurrencyAmount value={initialAmount} currency={currency} />,
                  1: <Translation {...TXTCLI_Casino} />,
                  2: description,
                  3: costFpp / 100
                }}
              />
            )}

            {redemptionLimit !== undefined && (
              <>
                {redemptionLimit === 0 && !!clearStakes && clearStakes > 0 && (
                  <Translation
                    tagName="p"
                    {...TXTCLI_You_will_need_to_wager_N0_in_t_ELL}
                    values={{
                      0: <CurrencyAmount value={clearStakes} currency={currency} />,
                      1: `${toLocaleDateString(language, expiredDate)} ${timeZone}`
                    }}
                  />
                )}
                {redemptionLimit > 0 && !!clearStakes && clearStakes > 0 && (
                  <Translation
                    tagName="p"
                    {...TXTCLI_You_will_need_to_wager_N0_in_t_ELL_3}
                    values={{
                      0: <CurrencyAmount value={clearStakes} currency={currency} />,
                      1: `${toLocaleDateString(language, expiredDate)} ${timeZone}`,
                      2: <CurrencyAmount value={redemptionLimit} currency={currency} />
                    }}
                  />
                )}
              </>
            )}

            {license === License.IT ||
              (license === License.PT && !!clearPoints && clearPoints > 0 && (
                <p>
                  <Translation
                    tagName="span"
                    {...TXTCLI_Winnings_from_the_Instant_Bonu_ELL}
                    values={{
                      0: clearPoints,
                      1: `${toLocaleDateString(language, expiredDate)} ${timeZone}`
                    }}
                  />{' '}
                  <Translation tagName="span" {...TXTCLI_Click_the_XAcceptX_button_to_g_ELL} />{' '}
                  <Translation tagName="span" {...TXTCLI_Good_LuckX} />
                </p>
              ))}

            {redemptionLimit !== undefined &&
              redemptionLimit > 0 &&
              !!clearPoints &&
              clearPoints > 0 && (
                <LicensedTranslation
                  exclude={[License.IT, License.USPA, License.PT]}
                  tagName="p"
                  {...TXTCLI_You_will_need_to_earn_N0_Redem_ELL_5}
                  values={{
                    0: clearPoints,
                    1: `${toLocaleDateString(language, expiredDate)} ${timeZone}`,
                    2: <CurrencyAmount value={redemptionLimit} currency={currency} />
                  }}
                />
              )}

            {eligibleGameNames && (
              <LicensedTranslation
                include={[License.IT]}
                tagName="p"
                {...TXTCLI_Eligible_GamesX_N0}
                values={{ 0: eligibleGameNames }}
              />
            )}

            {!!clearBets && clearBets > 0 && (
              <Translation
                tagName="p"
                {...TXTCLI_You_will_need_to_place_N0_Tota_ELL_2}
                values={{
                  0: 39 || clearBets,
                  1: `${toLocaleDateString(language, expiredDate)} ${timeZone}`
                }}
              />
            )}

            {redemptionLimit === 0 && !!clearPoints && clearPoints > 0 && (
              <Translation
                tagName="p"
                {...TXTCLI_You_will_need_to_earn_N0_Redem_ELL_2}
                values={{
                  0: clearPoints,
                  1: `${toLocaleDateString(language, expiredDate)} ${timeZone}`
                }}
              />
            )}

            <Translation tagName="p" {...TXTWCL_Please_indicate_if_you_would_l_ELL} />

            <LicensedTranslation
              include={[License.UK]}
              tagName="p"
              {...TXTCLI_By_accepting_this_offerX_you_a_ELL}
              values={{
                a: externalLink(
                  redemptionLimit === 0 ? instantBonusTermsCash : instantBonusTermsCashCap
                )
              }}
            />

            <Toggle
              id="bonus_accept_yes"
              name="bonusAccept"
              key={Accept.YES}
              label={
                <span>
                  <Translation {...TXTCLI_Accept} />
                </span>
              }
              type="radio"
              value={Accept.YES}
            />

            <Toggle
              id="bonus_accept_no"
              name="bonusAccept"
              key={Accept.NO}
              label={
                <span>
                  <Translation {...TXTCLI_Decline} />
                </span>
              }
              type="radio"
              value={Accept.NO}
            />

            <Translation
              tagName="p"
              {...TXTWCL_If_you_wish_to_see_more_inform_ELL}
              values={{
                a: translatedLink({
                  href: '/account/my-stars/bonus/instant-bonuses',
                  onClick: viewBonuses
                })
              }}
            />

            {hasError && errorCode !== ErrorCode.INSUFFICIENT_FUNDS && (
              <div className={sharedStyles.errorMessage}>
                <Translation {...TXTMOB_Something_went_wrongX_Please_t_ELL} />
              </div>
            )}

            {hasError && errorCode === ErrorCode.INSUFFICIENT_FUNDS && (
              <div className={sharedStyles.errorMessage}>
                <Translation {...TXTWCL_You_have_insufficient_balance__ELL} />
                <Translation {...TXTWCL_Balance_requiredX} />
                <CurrencyAmount
                  value={costCash || costFpp}
                  currency={costFpp ? Currency.PlayMoney : currency}
                />
              </div>
            )}

            {submitted ? (
              <Button className={sharedStyles.submitBtn} disabled translation={TXTCLI_LoadingXX} />
            ) : (
              <Buttons
                actionList={actions}
                notificationType={type}
                attributes={attributes}
                onClickPrimaryAction={() => handleSubmit(form)}
              />
            )}
          </>
        )}
      />
    </NotificationContainer>
  );
};

export default Issued;
